import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMediaQuery, useTheme } from "@mui/material";

const SidebarContext = createContext();

/** Width of the collapsed (icon-only) desktop rail. */
export const COLLAPSED_SIDEBAR_WIDTH = "66px";
/** Width of the expanded desktop sidebar per breakpoint. */
export const EXPANDED_SIDEBAR_WIDTH = { md: "220px", lg: "250px", xl: "350px" };
/** Width of the off-canvas drawer used on phones / small tablets. */
export const MOBILE_DRAWER_WIDTH = 280;

export function SidebarProvider({ children }) {
  const theme = useTheme();
  // Below `md` (900px) the sidebar becomes an off-canvas drawer instead of a
  // permanently visible rail, so the content gets the full viewport width.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Never leave the drawer open when the layout switches back to desktop.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleMobile = useCallback(() => setMobileOpen((prev) => !prev), []);

  const value = useMemo(() => {
    const desktopWidth = isCollapsed
      ? COLLAPSED_SIDEBAR_WIDTH
      : EXPANDED_SIDEBAR_WIDTH;

    return {
      isMobile,
      isCollapsed,
      setIsCollapsed,
      toggleCollapse,
      mobileOpen,
      openMobile,
      closeMobile,
      toggleMobile,
      // Width of the sidebar element itself.
      sidebarWidth: isMobile ? MOBILE_DRAWER_WIDTH : desktopWidth,
      // Space the page content must leave for the sidebar (none on mobile,
      // where the drawer overlays the content).
      contentMargin: isMobile ? 0 : desktopWidth,
    };
  }, [
    isMobile,
    isCollapsed,
    toggleCollapse,
    mobileOpen,
    openMobile,
    closeMobile,
    toggleMobile,
  ]);

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
